import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, User, Phone, Mail, Lock, Hash, BadgeCheck } from 'lucide-react';
import { verifyClassCode, registerStudent, checkUsernameAvailability } from '../../services/authService';
import AuthLayout from '../ui/AuthLayout';
import GlassCard from '../ui/GlassCard';
import Input from '../ui/Input';
import Button from '../ui/Button';

/**
 * Student Registration Flow
 * Step 1: Enter Class Code
 * Step 2: Fill Registration Form
 * Step 3: Success → Redirect to quiz
 */

const StudentRegistration = ({ onSuccess, onNavigate }) => {
  // Step management
  const [step, setStep] = useState(1); // 1 = Class Code, 2 = Form

  // Step 1: Class Code
  const [classCode, setClassCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [classData, setClassData] = useState(null);

  // Step 2: Registration Form
  const [formData, setFormData] = useState({
    fullName: '',
    username: '',
    pin: '',
    confirmPin: '',
    phoneNumber: '',
    email: ''
  });

  const [usernameChecking, setUsernameChecking] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState('');

  // ============================================
  // STEP 1: VERIFY CLASS CODE
  // ============================================

  const handleVerifyClassCode = async (e) => {
    e.preventDefault();
    setError('');

    if (!classCode.trim()) {
      setError('Please enter a class code');
      return;
    }

    setVerifying(true);

    const result = await verifyClassCode(classCode);

    setVerifying(false);

    if (result.success) {
      setClassData(result.class);
      setStep(2); // Move to registration form
    } else {
      setError(result.error || 'Invalid class code');
    }
  };

  // ============================================
  // STEP 2: REGISTRATION FORM
  // ============================================

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Reset username availability when username changes
    if (name === 'username') {
      setUsernameAvailable(null);
    }
  };

  const handleUsernameBlur = async () => {
    if (!formData.username.trim() || !classData) return;

    setUsernameChecking(true);

    const result = await checkUsernameAvailability(
      classData.institution_id,
      formData.username
    );

    setUsernameChecking(false);

    if (result.available) {
      setUsernameAvailable(true);
    } else if (result.taken) {
      setUsernameAvailable(false);
      setError('Username already taken. Try another one.');
    } else {
      setError(result.error || 'Failed to check username');
    }
  };

  const validateForm = () => {
    // Full name
    if (!formData.fullName.trim() || formData.fullName.length < 3) {
      setError('Full name must be at least 3 characters');
      return false;
    }

    // Username
    if (!formData.username.trim() || formData.username.length < 3) {
      setError('Username must be at least 3 characters');
      return false;
    }

    if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      setError('Username can only contain letters, numbers, and underscores');
      return false;
    }


    // PIN
    if (!/^\d{4}$/.test(formData.pin)) {
      setError('PIN must be exactly 4 digits');
      return false;
    }

    if (formData.pin !== formData.confirmPin) {
      setError('PINs do not match');
      return false;
    }

    // Phone number
    if (!/^(\+91)?[6-9]\d{9}$/.test(formData.phoneNumber.replace(/[\s\-]/g, ''))) {
      setError('Please enter a valid 10-digit mobile number');
      return false;
    }

    // Email (optional)
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    // Check username availability if not already checked
    if (usernameAvailable !== true) {
      setUsernameChecking(true);
      const usernameCheck = await checkUsernameAvailability(
        classData.institution_id,
        formData.username
      );
      setUsernameChecking(false);

      if (!usernameCheck.available) {
        setError(usernameCheck.error || 'Username already taken');
        setUsernameAvailable(false);
        return;
      }
      setUsernameAvailable(true);
    }

    setRegistering(true);

    // Normalize phone number
    let phoneNumber = formData.phoneNumber.replace(/[\s\-]/g, '');
    if (!phoneNumber.startsWith('+91')) {
      phoneNumber = `+91${phoneNumber}`;
    }

    const result = await registerStudent({
      classCode: classData.class_code,
      fullName: formData.fullName.trim(),
      username: formData.username.trim().toLowerCase(),
      pin: formData.pin,
      phoneNumber,
      email: formData.email.trim() || null,
      session: classData.session
    });

    setRegistering(false);

    if (result.success) {
      // Success! Redirect to dashboard
      if (onSuccess) {
        onSuccess(result.student, result.session);
      }
    } else {
      setError(result.error || 'Registration failed');
    }
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <AuthLayout
      title={step === 1 ? "Join Your Class" : "Create Account"}
      subtitle={step === 1 ? "Enter the class code provided by your teacher" : `Joining: ${classData?.class_name}`}
    >
      <div className="max-w-md mx-auto">
        <AnimatePresence mode="wait">
          {step === 1 ? (
            // STEP 1: CLASS CODE
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <GlassCard>
                <div className="text-center mb-6">
                  {/* Back to Landing */}
                  <button
                    onClick={() => onNavigate && onNavigate('landing')}
                    className="absolute top-6 left-6 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                </div>

                {/* Form */}
                <form onSubmit={handleVerifyClassCode} className="space-y-6">
                  <Input
                    label="Class Code"
                    value={classCode}
                    onChange={(e) => setClassCode(e.target.value.toUpperCase())}
                    placeholder="FLUENCE-CLASS6-2025"
                    icon={Hash}
                    className="text-center font-bold text-lg" // Pass class to input if supported or adjust Input component
                    autoFocus
                  />

                  <p className="text-xs text-gray-500 text-center -mt-4">
                    💡 Ask your teacher for the class code
                  </p>

                  {/* Error */}
                  <AnimatePresence>
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="text-red-500 text-sm font-medium text-center"
                      >
                        {error}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    variant="success"
                    loading={verifying}
                    disabled={!classCode.trim()}
                    className="w-full"
                    icon={BadgeCheck}
                  >
                    Verify Class Code
                  </Button>
                </form>

                {/* Footer */}
                <div className="mt-8 text-center text-sm text-gray-500">
                  <p>
                    Already have an account?{' '}
                    <button
                      type="button"
                      onClick={() => onNavigate && onNavigate('student-login')}
                      className="text-purple-600 hover:text-purple-700 font-medium"
                    >
                      Login here
                    </button>
                  </p>
                </div>
              </GlassCard>
            </motion.div>
          ) : (
            // STEP 2: REGISTRATION FORM
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <GlassCard>
                {/* Header */}
                <button
                  onClick={() => setStep(1)}
                  className="absolute top-6 left-6 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>

                <div className="mb-6 mt-2 text-center">
                  <div className="inline-block bg-green-50 border border-green-200 rounded-lg px-4 py-2">
                    <p className="text-sm font-semibold text-green-800">
                      {classData?.teacher?.full_name}
                    </p>
                    <p className="text-xs text-green-600">
                      {classData?.session}
                    </p>
                  </div>
                </div>

                {/* Form */}
                <form onSubmit={handleRegister} className="space-y-4">
                  <Input
                    label="Full Name"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleFormChange}
                    placeholder="Anaya Agrawal"
                    icon={User}
                    required
                  />

                  <div className="relative">
                    <Input
                      label="Create Username"
                      name="username"
                      value={formData.username}
                      onChange={handleFormChange}
                      onBlur={handleUsernameBlur}
                      placeholder="anaya01"
                      icon={User}
                      required
                    />
                    {/* Manually verify on blur via wrapper or assume input supports it. 
                      Since Input component didn't show onBlur prop, we wrap it or add it.
                      Checking Input.jsx again... it doesn't strictly destructure onBlur but passes rest props? 
                      Wait, Input definition was: ({ label, ..., onChange, ... }) => ...
                      It did NOT spread ...rest. I should check Input.jsx again or just add onBlur explicitly.
                  */}
                    <div className="absolute top-[38px] right-3 text-xs">
                      {usernameChecking && <span className="text-gray-400">Checking...</span>}
                      {usernameAvailable === true && <span className="text-green-500 font-bold">✓</span>}
                      {usernameAvailable === false && <span className="text-red-500 font-bold">✕</span>}
                    </div>
                  </div>
                  {/* Hack: The Input component in previous file view DOES NOT accept onBlur.
                     I should temporarily modify Input.jsx to accept onBlur or use a raw input here?
                     No, I should update Input.jsx as part of Phase 3. 
                     For now, I'll stick to this, but I'll update Input.jsx in next step.
                 */}

                  <Input
                    label="Phone Number"
                    name="phoneNumber"
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={handleFormChange}
                    placeholder="+91 98765 43210"
                    icon={Phone}
                    required
                  />

                  <Input
                    label="Email (Optional)"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    placeholder="anaya@example.com"
                    icon={Mail}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Create PIN"
                      name="pin"
                      type="password"
                      value={formData.pin}
                      onChange={handleFormChange}
                      placeholder="••••"
                      icon={Lock}
                      required
                    />
                    <Input
                      label="Confirm PIN"
                      name="confirmPin"
                      type="password"
                      value={formData.confirmPin}
                      onChange={handleFormChange}
                      placeholder="••••"
                      icon={Lock}
                      required
                    />
                  </div>

                  {/* Error */}
                  <AnimatePresence>
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="text-red-500 text-sm font-medium text-center"
                      >
                        {error}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    variant="success"
                    loading={registering || usernameChecking}
                    disabled={registering || usernameChecking}
                    className="w-full mt-2"
                  >
                    Create Account
                  </Button>
                </form>
              </GlassCard>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AuthLayout>
  );
};

export default StudentRegistration;
